import { lazy, Suspense } from "react";
import { MotionConfig } from "motion/react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { stripUrlQuery } from "@/lib/analyticsUrl";
import { ThemeProvider } from "next-themes";
import { CookieBanner } from "./components/CookieBanner";
import { StoreProvider, useStoreContext } from "@/hooks/useStoreContext";
import { useWizardCompleted } from "@/hooks/useWizardCompleted";
import { EmployeeProvider } from "@/hooks/useEmployeeContext";
import { MaintenanceGate } from "@/components/maintenance/MaintenanceGate";
import { SupportMfaGate } from "@/components/support/SupportMfaGate";

import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import { PageLoader } from "@/components/ui/page-loader";
import {
  DashboardSkeleton,
  EmployeesSkeleton,
  SuiviRhSkeleton,
  VacationsSkeleton,
  SettingsSkeleton,
} from "@/components/skeletons/PageSkeletons";
import { EmployeeDashboardSkeleton } from "@/components/skeletons/EmployeeDashboardSkeleton";

const Onboarding = lazy(() => import("./pages/Onboarding"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Confidentialite = lazy(() => import("./pages/Confidentialite"));
const CGV = lazy(() => import("./pages/CGV"));
const CGVVersion20260509 = lazy(() => import("./pages/CGVVersion20260509"));
const CGU = lazy(() => import("./pages/CGU"));
const ConventionIDCC1505 = lazy(() => import("./pages/ConventionIDCC1505"));
const AlternativeExcelPlanning = lazy(() => import("./pages/AlternativeExcelPlanning"));
const LogicielPlanningCarrefourExpress = lazy(
  () => import("./pages/LogicielPlanningCarrefourExpress"),
);
const LogicielPlanningCarrefourCity = lazy(
  () => import("./pages/LogicielPlanningCarrefourCity"),
);
const LogicielPlanningCarrefourContact = lazy(
  () => import("./pages/LogicielPlanningCarrefourContact"),
);
const FranchiseAlimentaireProximite = lazy(
  () => import("./pages/FranchiseAlimentaireProximite"),
);
const APropos = lazy(() => import("./pages/APropos"));
const Ressources = lazy(() => import("./pages/Ressources"));
const BlogGrilleSalarialeIdcc1505 = lazy(
  () => import("./pages/BlogGrilleSalarialeIdcc1505"),
);
const BlogHeuresSupplementairesIdcc1505 = lazy(
  () => import("./pages/BlogHeuresSupplementairesIdcc1505"),
);
const BlogTravailDimancheSuperette = lazy(
  () => import("./pages/BlogTravailDimancheSuperette"),
);
const BlogTempsPartielIdcc1505 = lazy(() => import("./pages/BlogTempsPartielIdcc1505"));
const BlogCongesPayesSuperette = lazy(() => import("./pages/BlogCongesPayesSuperette"));
const BlogRegistrePersonnelObligatoire = lazy(
  () => import("./pages/BlogRegistrePersonnelObligatoire"),
);
const BlogPreavisLicenciementIdcc1505 = lazy(
  () => import("./pages/BlogPreavisLicenciementIdcc1505"),
);
const BlogModulationTempsTravailCommerce = lazy(
  () => import("./pages/BlogModulationTempsTravailCommerce"),
);
const BlogSignatureElectroniquePlanning = lazy(
  () => import("./pages/BlogSignatureElectroniquePlanning"),
);
const BlogExportPaieSuperette = lazy(() => import("./pages/BlogExportPaieSuperette"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SuiviRh = lazy(() => import("./pages/SuiviRh"));
const DashboardConges = lazy(() => import("./pages/DashboardConges"));
const Employees = lazy(() => import("./pages/Employees"));
const ContractCreate = lazy(() => import("./pages/ContractCreate"));
const Planning = lazy(() => import("./pages/Planning"));
const PlanningRecommend = lazy(() => import("./pages/PlanningRecommend"));
const Reports = lazy(() => import("./pages/Reports"));
const ExportPaie = lazy(() => import("./pages/ExportPaie"));
const Settings = lazy(() => import("./pages/Settings"));
const CoverageModelEdit = lazy(() => import("./pages/CoverageModelEdit"));
const ManagerProfile = lazy(() => import("./pages/ManagerProfile"));
const SupportMode = lazy(() => import("./pages/SupportMode"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const EmployeeLeaves = lazy(() => import("./pages/EmployeeLeaves"));
const EmployeeProfile = lazy(() => import("./pages/EmployeeProfile"));
const EmployeeDocuments = lazy(() => import("./pages/EmployeeDocuments"));
const EmployeeTodo = lazy(() => import("./pages/EmployeeTodo"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 min default
      gcTime: 10 * 60 * 1000,   // 10 min garbage collection
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Garde : un franchisé qui n'a pas terminé le wizard d'onboarding est
// systématiquement renvoyé vers /onboarding. Sans elle, une navigation
// directe (URL tapée, PWA, lien) vers /dashboard contournait le wizard,
// le check n'existant qu'au login (Auth.redirectByRole).
const RequireWizardCompleted = ({ children }: { children: React.ReactNode }) => {
  const { user, userRole, supportAdminLabel } = useStoreContext();
  const location = useLocation();
  const isFranchisee = userRole === "franchisee" && !supportAdminLabel;
  const { isWizardComplete, isLoading, isError } = useWizardCompleted(isFranchisee ? user?.id : null);

  // Fail-open sur erreur : statut inconnu ⇒ on ne force pas l'onboarding.
  if (isFranchisee && !isLoading && !isError && !isWizardComplete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Wrapper for protected routes that need store context (franchisee).
// SupportMfaGate : le compte support ne voit rien avant son code de double
// authentification ; les autres comptes passent au travers.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <MaintenanceGate>
    <StoreProvider>
      <SupportMfaGate>
        <RequireWizardCompleted>{children}</RequireWizardCompleted>
      </SupportMfaGate>
    </StoreProvider>
  </MaintenanceGate>
);

// Wrapper for employee routes
const EmployeeRoute = ({ children }: { children: React.ReactNode }) => (
  <MaintenanceGate>
    <EmployeeProvider>{children}</EmployeeProvider>
  </MaintenanceGate>
);

// Pas de wrapper de transition de page : pour préserver la sensation
// instantanée des navigations vers des pages déjà en cache (React Query
// `staleTime: 2 min`, `gcTime: 10 min`). Toute animation d'opacité au mount
// retarde la perception de visibilité même quand les données sont prêtes.
const AppRoutes = () => {
  return (
    <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/cgv" element={<CGV />} />
          {/* Version précédente, applicable aux Clients déjà inscrits jusqu'à leur acceptation. */}
          <Route path="/cgv/2026-05-09" element={<CGVVersion20260509 />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/convention-idcc-1505" element={<ConventionIDCC1505 />} />
          <Route path="/alternative-excel-planning" element={<AlternativeExcelPlanning />} />
          <Route
            path="/logiciel-planning-carrefour-express"
            element={<LogicielPlanningCarrefourExpress />}
          />
          <Route
            path="/logiciel-planning-carrefour-city"
            element={<LogicielPlanningCarrefourCity />}
          />
          <Route
            path="/logiciel-planning-carrefour-contact"
            element={<LogicielPlanningCarrefourContact />}
          />
          <Route
            path="/franchise-alimentaire-proximite"
            element={<FranchiseAlimentaireProximite />}
          />
          <Route path="/a-propos" element={<APropos />} />
          <Route path="/ressources" element={<Ressources />} />
          <Route
            path="/blog/grille-salariale-idcc-1505-2026"
            element={<BlogGrilleSalarialeIdcc1505 />}
          />
          <Route
            path="/blog/heures-supplementaires-idcc-1505"
            element={<BlogHeuresSupplementairesIdcc1505 />}
          />
          <Route
            path="/blog/travail-dimanche-superette"
            element={<BlogTravailDimancheSuperette />}
          />
          <Route path="/blog/temps-partiel-idcc-1505" element={<BlogTempsPartielIdcc1505 />} />
          <Route path="/blog/conges-payes-superette" element={<BlogCongesPayesSuperette />} />
          <Route
            path="/blog/registre-personnel-obligatoire"
            element={<BlogRegistrePersonnelObligatoire />}
          />
          <Route
            path="/blog/preavis-licenciement-idcc-1505"
            element={<BlogPreavisLicenciementIdcc1505 />}
          />
          <Route
            path="/blog/modulation-temps-travail-commerce"
            element={<BlogModulationTempsTravailCommerce />}
          />
          <Route
            path="/blog/signature-electronique-planning"
            element={<BlogSignatureElectroniquePlanning />}
          />
          <Route path="/blog/export-paie-superette" element={<BlogExportPaieSuperette />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<DashboardSkeleton />}>
                  <Dashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/suivi-rh"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SuiviRhSkeleton />}>
                  <SuiviRh />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/conges"
            element={
              <ProtectedRoute>
                <Suspense fallback={<VacationsSkeleton />}>
                  <DashboardConges />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Suspense fallback={<EmployeesSkeleton />}>
                  <Employees />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/contracts/new"
            element={
              <ProtectedRoute>
                <ContractCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:employeeId"
            element={
              <ProtectedRoute>
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <Planning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning/recommend"
            element={
              <ProtectedRoute>
                <PlanningRecommend />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/export-paie"
            element={
              <ProtectedRoute>
                <ExportPaie />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/coverage-models/new"
            element={
              <ProtectedRoute>
                <CoverageModelEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/coverage-models/:modelId"
            element={
              <ProtectedRoute>
                <CoverageModelEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<SettingsSkeleton />}>
                  <Settings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ManagerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportMode />
              </ProtectedRoute>
            }
          />
          {/* Employee Routes */}
          <Route
            path="/employee"
            element={
              <EmployeeRoute>
                <Suspense fallback={<EmployeeDashboardSkeleton />}>
                  <EmployeeDashboard />
                </Suspense>
              </EmployeeRoute>
            }
          />
          <Route
            path="/employee/todo"
            element={
              <EmployeeRoute>
                <EmployeeTodo />
              </EmployeeRoute>
            }
          />
          <Route
            path="/employee/leaves"
            element={
              <EmployeeRoute>
                <EmployeeLeaves />
              </EmployeeRoute>
            }
          />
          <Route
            path="/employee/profile"
            element={
              <EmployeeRoute>
                <EmployeeProfile />
              </EmployeeRoute>
            }
          />
          <Route
            path="/employee/documents"
            element={
              <EmployeeRoute>
                <EmployeeDocuments />
              </EmployeeRoute>
            }
          />
          <Route path="/billing" element={<Navigate to="/settings?section=facturation" replace />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// MotionConfig reducedMotion="user" : quand le système demande moins
// d'animations, motion/react coupe translations, échelles et layout et ne garde
// que les fondus — même règle que le bloc prefers-reduced-motion d'index.css.
const App = () => (
  <MotionConfig reducedMotion="user">
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="equiply-theme">
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader text="Chargement de l'application..." className="min-h-screen" />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
      <SpeedInsights beforeSend={stripUrlQuery} />
      <Analytics beforeSend={stripUrlQuery} />
      <CookieBanner />
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  </ThemeProvider>
  </MotionConfig>
);

export default App;
